import { Controller } from "@hotwired/stimulus"
import { showFlash } from "./flash_controller"

export default class extends Controller {
  static targets = ["triggerButton", "popover", "toggleButton", "toggleKnob"]
  static values = {
    buyerId: String,
    sellerId: String,
    enabled: Boolean
  }

  connect() {
    this.boundHandlePointerDown = this.handlePointerDown.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('pointerdown', this.boundHandlePointerDown, true)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener('pointerdown', this.boundHandlePointerDown, true)
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  togglePopover(event) {
    event.stopPropagation()
    
    if (this.popoverTarget.classList.contains('hidden')) {
      this.showPopover()
    } else {
      this.hidePopover()
    }
  }

  showPopover() {
    this.closeAllOtherPopovers()
    this.popoverTarget.classList.remove('hidden')
    this.triggerButtonTarget.setAttribute('aria-expanded', 'true')
  }

  closeAllOtherPopovers() {
    document.querySelectorAll('[data-controller*="review-requirement-toggle"]').forEach(element => {
      if (element !== this.element) {
        const popover = element.querySelector('[data-review-requirement-toggle-target="popover"]')
        if (popover && !popover.classList.contains('hidden')) {
          popover.classList.add('hidden')
          element.querySelector('[data-review-requirement-toggle-target="triggerButton"]')?.setAttribute('aria-expanded', 'false')
        }
      }
    })
  }

  hidePopover() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add('hidden')
      this.triggerButtonTarget.setAttribute('aria-expanded', 'false')
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || this.popoverTarget.classList.contains('hidden')) return

    event.preventDefault()
    this.hidePopover()
    this.triggerButtonTarget.focus()
  }

  handlePointerDown(event) {
    if (!this.element.contains(event.target) && !this.popoverTarget.classList.contains('hidden')) {
      this.hidePopover()
    }
  }

  async toggle() {
    const newState = !this.enabledValue
    
    // Optimistic update - change UI immediately
    if (this.hasToggleButtonTarget && this.hasToggleKnobTarget) {
      this.updateToggleVisualState(newState)
    }
    // Keep popover open - user might want to toggle again
    
    // Save to server immediately
    try {
      const response = await fetch("/buyer_seller_settings/toggle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          buyer_id: this.buyerIdValue,
          seller_id: this.sellerIdValue,
          requires_review: newState
        })
      })

      const data = await response.json()

      if (data.success) {
        // Success - update the enabled value (no visual feedback needed)
        this.enabledValue = newState
      } else {
        // Server error - revert the visual state
        this.revertToggleState(!newState)
        showFlash(data.error || "Klaida keičiant peržiūros reikalavimą", 'error')
      }
    } catch (error) {
      console.error("Error saving review requirement:", error)
      // Network error - revert the visual state
      this.revertToggleState(!newState)
      showFlash("Nepavyko pakeisti peržiūros reikalavimo. Bandykite dar kartą.", 'error')
    }
  }

  updateToggleVisualState(enabled) {
    // Update toggle button classes
    if (enabled) {
      this.toggleButtonTarget.classList.remove('bg-gray-200')
      this.toggleButtonTarget.classList.add('bg-gray-900')
      this.toggleKnobTarget.classList.remove('translate-x-0')
      this.toggleKnobTarget.classList.add('translate-x-4')
    } else {
      this.toggleButtonTarget.classList.remove('bg-gray-900')
      this.toggleButtonTarget.classList.add('bg-gray-200')
      this.toggleKnobTarget.classList.remove('translate-x-4')
      this.toggleKnobTarget.classList.add('translate-x-0')
    }
    
    // Update aria-checked attribute
    this.toggleButtonTarget.setAttribute('aria-checked', enabled)
  }



  revertToggleState(originalState) {
    // Revert visual state on error
    if (this.hasToggleButtonTarget && this.hasToggleKnobTarget) {
      this.updateToggleVisualState(originalState)
    }
    this.enabledValue = originalState
  }
}
